/* Adds overflow-only controls to desktop product thumbnail rails.
   Main-image switching and selected-thumbnail synchronization stay native.
   Also adds the "1/16" counter badge from the Yeha Mart template to the main gallery. */
const railSelector = 'media-gallery slideshow-controls[thumbnails]:is([pagination-position="left"], [pagination-position="right"])';
const desktop = window.matchMedia('(min-width: 750px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const installed = new WeakMap();
const counters = new WeakMap();

function setupRail(controls) {
  if (installed.has(controls)) return;
  const container = controls.querySelector('.slideshow-controls__thumbnails-container');
  const thumbnails = controls.querySelector('.slideshow-controls__thumbnails');
  if (!container || !thumbnails) return;
  if (installed.get(controls) === container) return;
  controls.querySelectorAll('.pdp-vg__thumb-nav').forEach((button) => button.remove());
  installed.set(controls, container);
  controls.setAttribute('data-yeha-rail', '');

  const createButton = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pdp-vg__thumb-nav ' + (direction < 0 ? 'pdp-vg__thumb-up' : 'pdp-vg__thumb-down');
    button.setAttribute('aria-label', direction < 0 ? 'Scroll product thumbnails up' : 'Scroll product thumbnails down');
    const template = document.createElement('template');
    template.innerHTML = direction < 0 ? "<svg width=\"14\" height=\"9\" viewBox=\"0 0 14 9\" xmlns=\"http://www.w3.org/2000/svg\" aria-hidden=\"true\"><path d=\"M7 1.2L13 7.2L11.9 8.3L7 3.4L2.1 8.3L1 7.2Z\"/></svg>" : "<svg width=\"14\" height=\"9\" viewBox=\"0 0 14 9\" xmlns=\"http://www.w3.org/2000/svg\" aria-hidden=\"true\"><path d=\"M7 7.8L1 1.8L2.1 0.7L7 5.6L11.9 0.7L13 1.8Z\"/></svg>";
    button.append(template.content.cloneNode(true));
    button.addEventListener('click', () => {
      container.scrollBy({top: direction * Math.max(72, container.clientHeight * 0.65), behavior: reducedMotion.matches ? 'instant' : 'smooth'});
    });
    return button;
  };

  const previous = createButton(-1);
  const next = createButton(1);
  container.before(previous);
  container.after(next);

  let frame = 0;
  const update = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      if (!controls.isConnected) return;
      const overflow = desktop.matches && container.scrollHeight > container.clientHeight + 3;
      previous.hidden = !overflow;
      next.hidden = !overflow;
      const atStart = container.scrollTop <= 2;
      const atEnd = container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
      previous.dataset.edge = String(atStart);
      next.dataset.edge = String(atEnd);
      previous.disabled = atStart;
      next.disabled = atEnd;
    });
  };
  container.addEventListener('scroll', update, {passive: true});
  desktop.addEventListener('change', update);
  const resize = new ResizeObserver(update);
  resize.observe(container);
  resize.observe(thumbnails);
  const mutation = new MutationObserver(update);
  mutation.observe(thumbnails, {childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style', 'aria-selected']});
  update();

  const cleanup = (event) => {
    if (!event.target.contains(controls)) return;
    resize.disconnect();
    mutation.disconnect();
    desktop.removeEventListener('change', update);
    container.removeEventListener('scroll', update);
    document.removeEventListener('shopify:section:unload', cleanup);
    cancelAnimationFrame(frame);
  };
  document.addEventListener('shopify:section:unload', cleanup);
}

/* "7/16" counter badge on the main image (from Yeha Mart .pdp-vg__counter) */
function setupCounter(slideshowContainer) {
  if (counters.has(slideshowContainer)) return;
  const badge = document.createElement('div');
  badge.className = 'pdp-vg__counter';
  badge.setAttribute('aria-live', 'polite');
  badge.hidden = true;
  const current = document.createElement('span');
  const total = document.createElement('span');
  badge.append(current, document.createTextNode('/'), total);
  slideshowContainer.append(badge);

  const update = () => {
    const slides = Array.from(slideshowContainer.querySelectorAll('slideshow-slide'));
    const count = slides.length;
    badge.hidden = count < 2;
    if (count < 2) return;
    let index = slides.findIndex((slide) => slide.getAttribute('aria-hidden') !== 'true');
    if (index < 0) index = 0;
    const currentText = String(index + 1);
    const totalText = String(count);
    // Only write when the text changes, so the observer below does not loop.
    if (current.textContent !== currentText) current.textContent = currentText;
    if (total.textContent !== totalText) total.textContent = totalText;
  };
  const observer = new MutationObserver(update);
  observer.observe(slideshowContainer, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden']});
  counters.set(slideshowContainer, observer);
  update();

  const cleanup = (event) => {
    if (!event.target.contains(slideshowContainer)) return;
    observer.disconnect();
    document.removeEventListener('shopify:section:unload', cleanup);
  };
  document.addEventListener('shopify:section:unload', cleanup);
}

function initialize(root = document) {
  root.querySelectorAll(railSelector).forEach(setupRail);
  root.querySelectorAll('media-gallery slideshow-container').forEach(setupCounter);
  root.querySelectorAll('media-gallery slideshow-arrows .slideshow-control').forEach((button) => {
    if (button.dataset.yehaOldArrow) return;
    const template = document.createElement('template');
    template.innerHTML = button.classList.contains('slideshow-control--previous') ? "<svg width=\"14\" height=\"26\" viewBox=\"0 0 14 26\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M13.7071 0.292893C14.0976 0.683417 14.0976 1.31658 13.7071 1.70711L2.41421 13L13.7071 24.2929C14.0976 24.6834 14.0976 25.3166 13.7071 25.7071C13.3166 26.0976 12.6834 26.0976 12.2929 25.7071L0.292893 13.7071C-0.0976311 13.3166 -0.0976311 12.6834 0.292893 12.2929L12.2929 0.292893C12.6834 -0.0976311 13.3166 -0.0976311 13.7071 0.292893Z\" /></svg>" : "<svg width=\"14\" height=\"26\" viewBox=\"0 0 14 26\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M0.292893 0.292893C0.683417 -0.0976311 1.31658 -0.0976311 1.70711 0.292893L13.7071 12.2929C14.0976 12.6834 14.0976 13.3166 13.7071 13.7071L1.70711 25.7071C1.31658 26.0976 0.683417 26.0976 0.292893 25.7071C-0.0976311 25.3166 -0.0976311 24.6834 0.292893 24.2929L11.5858 13L0.292893 1.70711C-0.0976311 1.31658 -0.0976311 0.683417 0.292893 0.292893Z\" /></svg>";
    const svg = template.content.firstElementChild;
    svg.setAttribute('aria-hidden','true');
    button.replaceChildren(svg);
    button.dataset.yehaOldArrow = 'true';
  });
}
initialize();
document.addEventListener('shopify:section:load', (event) => initialize(event.target));

// Reconnect rails after dynamic product/variant gallery rendering in the editor.
let initializeFrame = 0;
const galleryObserver = new MutationObserver((records) => {
  if (!records.some((record) => Array.from(record.addedNodes).some((node) => node.nodeType === 1))) return;
  cancelAnimationFrame(initializeFrame);
  initializeFrame = requestAnimationFrame(() => initialize());
});
galleryObserver.observe(document.body, {childList: true, subtree: true});
